import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { logout } from "../redux/slices/loginSlice";
import {
  openProjectDrawer,
  resetProjects,
  selectProject,
} from "../redux/slices/projectsSlice";
import { canSwitchSource } from "../redux/slices/sourceSlice";
import SourceSwitch from "./SourceSwitch";
import { Avatar } from "../ui";

import "./PortalShell.css";

/**
 * PortalShell — the Employee Hub layout.
 *
 * A slide-out navigation drawer (not a permanent sidebar), a slim white top
 * bar with the brand, the data-source switch and the account menu, then the
 * page content in the shared TPC shell. The page itself owns its heading, as
 * the reference pages do with HubHeader / PageShell.
 */

type NavItem = {
  label: string;
  to: string;
  icon: ReactNode;
};

type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
};

const Icon = ({ d }: { d: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const PATHS = {
  home: "M3 12l9-9 9 9M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10",
  chart: "M3 17l6-6 4 4 8-8",
  customers:
    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  invoice:
    "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  waiver:
    "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  audit:
    "M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10m0-10a2 2 0 012 2h2a2 2 0 012-2V7",
  folder:
    "M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z",
  users:
    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  sync:
    "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  settings:
    "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  money:
    "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  compass:
    "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7",
  Chevron: "M19 9l-7 7-7-7",
  menu: "M4 6h16M4 12h16M4 18h16",
  close: "M6 18L18 6M6 6l12 12",
  gear:
    "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
} as const;

const COLLAPSE_KEY = "portal.nav.collapsed";

function readCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}");
  } catch {
    return {};
  }
}

export default function PortalShell({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.login);
  const { projects, selectedProject } = useAppSelector((state) => state.projects);
  const forbidden = useAppSelector((state) => state.source.forbidden);

  const [navOpen, setNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const permissions = user?.permissions || {};
  const isSuper = user?.role === "super-admin";
  const home = isAdmin ? "/dashboard" : "/project-home";

  useEffect(() => {
    setNavOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [navOpen]);

  useEffect(() => {
    const onOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const sections = useMemo<NavSection[]>(() => {
    if (!isAdmin) {
      return [
        {
          id: "project",
          label: "Your project",
          items: [
            { label: "Project Home", to: "/project-home", icon: <Icon d={PATHS.home} /> },
            { label: "Financial", to: "/project-home/financial", icon: <Icon d={PATHS.money} /> },
            { label: "Documents", to: "/project-home/documents", icon: <Icon d={PATHS.folder} /> },
            { label: "What to Expect", to: "/project-home/meet-your-team", icon: <Icon d={PATHS.compass} /> },
          ],
        },
        {
          id: "account",
          label: "Account",
          items: [
            { label: "My Settings", to: "/dashboard/account-settings", icon: <Icon d={PATHS.settings} /> },
          ],
        },
      ];
    }

    const vendors: NavItem[] = [
      ...(permissions["lien waiver"] || isSuper
        ? [{ label: "Lien Waivers", to: "/dashboard/lien-waivers", icon: <Icon d={PATHS.waiver} /> }]
        : []),
      ...(permissions["vendors"] || isSuper
        ? [{ label: "Vendor Audit", to: "/dashboard/vendor-audit", icon: <Icon d={PATHS.audit} /> }]
        : []),
    ];

    const administration: NavItem[] = [
      { label: "Users", to: "/dashboard/users", icon: <Icon d={PATHS.users} /> },
      ...(permissions["integrations"] || isSuper
        ? [{ label: "Integrations", to: "/dashboard/integrations", icon: <Icon d={PATHS.sync} /> }]
        : []),
    ];

    return [
      {
        id: "main",
        label: "Main",
        items: [
          { label: "Dashboard", to: "/dashboard", icon: <Icon d={PATHS.home} /> },
          { label: "Client Overview", to: "/dashboard/client-overview", icon: <Icon d={PATHS.chart} /> },
        ],
      },
      {
        id: "billing",
        label: "Billing",
        items: [
          { label: "Customers", to: "/dashboard/customers", icon: <Icon d={PATHS.customers} /> },
          { label: "Invoices", to: "/dashboard/invoices", icon: <Icon d={PATHS.invoice} /> },
        ],
      },
      ...(vendors.length > 0 ? [{ id: "vendors", label: "Vendors", items: vendors }] : []),
      {
        id: "documents",
        label: "Documents",
        items: [
          { label: "Dropbox Folders", to: "/dashboard/dropbox-folders", icon: <Icon d={PATHS.folder} /> },
          { label: "Document Library", to: "/dashboard/document-library", icon: <Icon d={PATHS.folder} /> },
        ],
      },
      { id: "admin", label: "Administration", items: administration },
      {
        id: "account",
        label: "Account",
        items: [
          { label: "My Settings", to: "/dashboard/account-settings", icon: <Icon d={PATHS.settings} /> },
        ],
      },
    ];
  }, [isAdmin, isSuper, permissions]);

  const isActive = (to: string) => {
    if (to === "/dashboard" || to === "/project-home") return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const toggleSection = (id: string) => {
    setCollapsed((current) => {
      const next = { ...current, [id]: !current[id] };
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      } catch {
        /* private window */
      }
      return next;
    });
  };

  const signOut = () => {
    setMenuOpen(false);
    dispatch(logout());
    dispatch(resetProjects());
    navigate("/login");
  };

  const closeNav = () => setNavOpen(false);

  return (
    <div className="portal-shell">
      <header className="portal-topbar">
        <div className="portal-topbar__left">
          <button
            type="button"
            className="portal-icon-btn"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={PATHS.menu} />
            </svg>
          </button>

          <Link to={home} className="portal-brand">
            <img src="/logo.png" alt="" className="portal-brand__logo" />
            <span className="portal-brand__text">
              <span>The Pinnacle Companies</span>
              <strong>Client Portal</strong>
            </span>
          </Link>
        </div>

        <div className="portal-topbar__right">
          {!isAdmin && projects.length > 1 ? (
            <label className="portal-project-picker">
              <span className="portal-project-picker__label">Project</span>
              <select
                value={selectedProject?.id || ""}
                onChange={(event) => {
                  const project = projects.find(
                    (candidate: any) => candidate.id === event.target.value,
                  );
                  if (project) dispatch(selectProject(project));
                }}
                aria-label="Project"
              >
                {projects.map((project: any) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {canSwitchSource(user) && !forbidden ? <SourceSwitch /> : null}

          <div className="portal-user" ref={menuRef}>
            <button
              type="button"
              className="portal-user__button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <Avatar src={user?.photo} name={user?.username} size="sm" />
              <span className="portal-user__name">
                <strong>{user?.username || "Account"}</strong>
                <span>{user?.role || ""}</span>
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d={PATHS.Chevron} />
              </svg>
            </button>

            {menuOpen && (
              <div className="portal-user__menu" role="menu">
                <div className="portal-user__head">
                  <Avatar src={user?.photo} name={user?.username} />
                  <div>
                    <strong>{user?.username || "Account"}</strong>
                    <span>{user?.email || ""}</span>
                  </div>
                </div>
                <Link
                  to="/dashboard/account-settings"
                  className="portal-user__action"
                  onClick={() => setMenuOpen(false)}
                >
                  My settings
                </Link>
                <button type="button" className="portal-user__action" onClick={signOut}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div
        className={`portal-nav__overlay${navOpen ? " is-open" : ""}`}
        onClick={closeNav}
        aria-hidden
      />

      <aside className={`portal-nav${navOpen ? " is-open" : ""}`} aria-label="Portal navigation">
        <div className="portal-nav__header">
          <button type="button" className="portal-nav__brand" onClick={() => { navigate(home); closeNav(); }}>
            <span className="portal-nav__eyebrow">The Pinnacle Companies</span>
            <span className="portal-nav__title">Client Portal</span>
          </button>
          <button type="button" className="portal-nav__close" onClick={closeNav} aria-label="Close navigation">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={PATHS.close} />
            </svg>
          </button>
        </div>

        <nav className="portal-nav__body">
          {sections.map((section) => {
            const isCollapsed = collapsed[section.id] ?? false;
            return (
              <div key={section.id} className="portal-nav__section">
                <button
                  type="button"
                  className="portal-nav__section-head"
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={!isCollapsed}
                >
                  <span>{section.label}</span>
                  <svg
                    className={`portal-nav__chev${isCollapsed ? " is-collapsed" : ""}`}
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={PATHS.Chevron} />
                  </svg>
                </button>

                {!isCollapsed &&
                  section.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`portal-nav__item${isActive(item.to) ? " is-active" : ""}`}
                      onClick={closeNav}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}

                {section.id === "project" && !isCollapsed && projects.length > 0 && (
                  <div className="portal-nav__sublabel">Projects</div>
                )}

                {section.id === "project" &&
                  !isCollapsed &&
                  projects.map((project: any) => (
                    <div key={project.id} className="portal-nav__project">
                      <button
                        type="button"
                        className={`portal-nav__item${selectedProject?.id === project.id ? " is-active" : ""}`}
                        onClick={() => {
                          dispatch(selectProject(project));
                          if (location.pathname !== "/project-home") navigate("/project-home");
                          closeNav();
                        }}
                      >
                        <span className="portal-nav__project-mark" />
                        <span>{project.name}</span>
                      </button>
                      <button
                        type="button"
                        className="portal-nav__gear"
                        aria-label={`Settings for ${project.name}`}
                        onClick={() => {
                          dispatch(openProjectDrawer(project));
                          closeNav();
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d={PATHS.gear} />
                        </svg>
                      </button>
                    </div>
                  ))}
              </div>
            );
          })}
        </nav>

        <div className="portal-nav__footer">
          <p>{isAdmin ? "Client Portal · Internal" : "Client Portal"}</p>
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="portal-main">
        <div className="ui-shell ui-shell--full">{children}</div>
      </main>

      <footer className="portal-footer">
        <span>The Pinnacle Companies · Client Portal</span>
      </footer>
    </div>
  );
}
